
import { defineComponent } from 'vue'
import SubscriptionMixin from '~/mixins/subscriptionMixin'

import { StripeCardElementChangeEvent } from '@stripe/stripe-js'
import ActiveUserMixin from '~/mixins/activeUserMixin'
import OwnerBillingMixin from '~/mixins/ownerBillingMixin'
import { SubscriptionCheckoutPayload, SubscriptionStatusChoice } from '~/types/types'
import StripeCardInput from './StripeCardInput.vue'

export default defineComponent({
  name: 'StripeCheckoutBox',
  mixins: [SubscriptionMixin, OwnerBillingMixin, ActiveUserMixin],
  props: {
    planSlug: {
      type: String,
      required: true
    },
    coupon: {
      type: String,
      required: true
    }
  },
  components: {
    StripeCardInput
  },
  computed: {
    enableCheckout(): boolean {
      return Boolean(
        this.billingEmail && this.validBillingEmail && this.fullName && this.validCardDetails
      )
    },
    stripeInput(): typeof StripeCardInput {
      return this.$refs['checkout-box-stripe-input'] as unknown as typeof StripeCardInput
    }
  },
  data() {
    return {
      billingEmail: '',
      validBillingEmail: true,
      fullName: '',
      cardDetails: '',
      validCardDetails: false,
      cardInputFocus: false,
      checkoutLoading: false,
      checkoutState: null as null | 'SUCCESS' | 'FAIL' | 'PARTIAL'
    }
  },
  async fetch(): Promise<void> {
    const { owner, provider } = this.$route.params
    await this.fetchOwnerDetails({
      login: owner,
      provider,
      fetchPrimaryUser: true
    })

    if (this.owner.primaryUser) {
      this.billingEmail = this.owner.primaryUser.email
      this.fullName = this.owner.primaryUser.fullName ?? ''
    }
  },
  methods: {
    handleStripeCardChange(event: StripeCardElementChangeEvent): void {
      this.validCardDetails = event.error ? false : true
    },
    validateEmail(el: HTMLInputElement): void {
      this.validBillingEmail = el.checkValidity()
    },
    async tempAwaitStripeScaUpdate(): Promise<void> {
      let RETRY_COUNT = 5
      const { owner, provider } = this.$route.params
      const retryFunction = async (): Promise<void> => {
        const statusResponse = await this.fetchBillingStatus({
          login: owner,
          provider,
          refetch: true
        })
        if (RETRY_COUNT >= 0 && statusResponse.status === SubscriptionStatusChoice.ScaRequired) {
          RETRY_COUNT -= 1
          await new Promise((resolve) => setTimeout(resolve, 1000))
          await retryFunction()
        }
        return
      }
      await retryFunction()
    },
    /**
     * Checks out the user with their given payment details and attempts verification of the payment.
     * - Updates `checkoutState` to a valid state based on if the payment succeded or not.
     * - Redirects user to the billing page if payment succeded or SCA is pending.
     * - Shows an error message if payment failed.
     *
     * @returns {Promise<void>}
     */
    async checkout(): Promise<void> {
      this.checkoutLoading = true
      this.checkoutState = null
      if (!this.owner.vcsInstallationId) {
        await this.$fetch
      }
      try {
        const res = await this.$stripe?.createToken(this.stripeInput.cardElement, {
          name: this.fullName
        })
        if (res?.error) {
          throw res.error
        }

        if (res?.token) {
          const params = {
            email: this.billingEmail,
            name: this.fullName,
            token: res.token.id,
            seats: this.seats,
            coupon: this.coupon ?? '',
            planSlug: this.planSlug,
            installationId: this.owner.vcsInstallationId ?? ''
          }
          const checkoutResponse: SubscriptionCheckoutPayload =
            await this.subscriptionCheckout(params)

          const { provider, owner } = this.$route.params
          this.fetchBillingDetails({
            provider,
            login: owner,
            refetch: true
          })

          let redirectRoute = this.$generateRoute([])

          if (checkoutResponse.nextAction === 'BILLING') {
            await this.fetchActiveUser({ refetch: true })
            this.checkoutState = 'SUCCESS'
            this.$toast.success('🎉 Subscription activated! Redirecting you to the billing page.')
            redirectRoute = this.$generateRoute(['settings', 'billing'], false, true)
            setTimeout(() => {
              this.$router.push(redirectRoute)
            }, 1200)
          } else if (checkoutResponse.nextAction === 'ACTIVATE_NEW_REPO') {
            await this.fetchActiveUser({ refetch: true })
            this.checkoutState = 'SUCCESS'
            this.$toast.success(
              "🎉 Subscription activated! Let's activate a repository and get started."
            )
            redirectRoute = this.$generateRoute(['repos'], false, true)
            setTimeout(() => {
              this.$router.push(redirectRoute)
            }, 1200)
          } else if (checkoutResponse.nextAction === 'SCA') {
            const result = await this.$stripe?.confirmCardPayment(
              checkoutResponse.clientSecret as string
            )

            if (result?.paymentIntent && result.paymentIntent.status === 'succeeded') {
              await this.tempAwaitStripeScaUpdate()
              await this.fetchActiveUser({ refetch: true })
              this.checkoutState = 'SUCCESS'
              this.$toast.success('🎉 Subscription activated! Redirecting you to the billing page.')
              setTimeout(() => {
                this.$router.push(this.$generateRoute(['settings', 'billing'], false, true))
              }, 1200)
            } else {
              await this.fetchActiveUser({ refetch: true })
              this.checkoutState = 'PARTIAL'
              if (result?.error?.message) {
                this.$toast.show({
                  type: 'danger',
                  message: result.error.message,
                  timeout: 10
                })
              } else {
                this.$toast.show({
                  type: 'danger',
                  message:
                    'We are unable to authenticate your payment method. Please choose a different payment method and try again.',
                  timeout: 6
                })
              }
              setTimeout(() => {
                this.$router.push(this.$generateRoute(['settings', 'billing'], false, true))
              }, 6200)
            }
          }
        }
      } catch (e) {
        this.checkoutState = 'FAIL'
        const err = e as Error
        try {
          const errorMessage = JSON.parse(err.message.replace('GraphQL error: ', ''))
          if (Array.isArray(errorMessage)) {
            this.$toast.danger(errorMessage.join('\n'))
          } else {
            this.$toast.danger(errorMessage)
          }
        } catch (_parseError) {
          if (err.message) {
            this.$toast.show({
              type: 'danger',
              message: err.message.replace('GraphQL error: ', ''),
              timeout: 10
            })
          } else {
            this.$toast.danger(
              'Something went wrong processing your card payment. Please check your card details or contact support.'
            )
          }
        } finally {
          this.logErrorForUser(e as Error, 'Billing error', {})
        }
      } finally {
        this.checkoutLoading = false
      }
    }
  }
})
